import { OP_CELLS_MAX, OP_FRAME_MAX, PATCH_CELL_RUNS_MAX, PATCH_PALETTE_MAX, PATCH_VALUE_RUNS_MAX, type CellPatchIntent, type PatchGrid, type PatchValue } from "./op-types.ts";

/**
 * Cells run-encoded into cell patches (ADR-086 decision 20): a list of cells with their values becomes as few
 * patches as hold them, each within the patch's limits (cell runs, value runs, palette) and short enough for
 * one operation frame. A patch is cut at whichever limit the next cell would pass.
 */

/**
 * The most code units an operation's own fields take around its intents: the braces, the keys and two ids of
 * the longest form.
 */
export const OP_FRAME_OVERHEAD = 96;

/**
 * How many decimal digits a non-negative integer below 2^53 is written with.
 *
 * @param value The integer.
 * @returns The digit count.
 */
function digits(value: number): number {
  let count = 1;
  for (let rest = value; rest >= 10; rest = Math.floor(rest / 10)) {
    count += 1;
  }
  return count;
}

/**
 * The cells a patch is built from: how many there are, each one's index in the grid in strictly ascending
 * order, a number that is equal for two cells exactly when they take the same value, and the value a number
 * stands for.
 */
export interface PatchCells {
  readonly count: number;
  readonly indexAt: (position: number) => number;
  readonly codeAt: (position: number) => number;
  readonly valueOf: (code: number) => PatchValue;
}

/**
 * A patch being built, with the code units its palette and runs take so far.
 */
interface OpenPatch {
  readonly slots: Map<number, number>;
  readonly palette: PatchValue[];
  readonly cells: [number, number][];
  readonly values: [number, number][];
  units: number;
  count: number;
}

/**
 * An empty patch.
 *
 * @returns The patch.
 */
function openPatch(): OpenPatch {
  return { slots: new Map(), palette: [], cells: [], values: [], units: 0, count: 0 };
}

/**
 * Adds a cell to a patch when it fits.
 *
 * @param patch The patch, changed in place when the cell fits.
 * @param index The cell's index.
 * @param code The number of the cell's value.
 * @param source The cells, for the value a number stands for.
 * @param budget The most code units the patch's palette and runs may take.
 * @returns False when the cell would pass a limit, the budget or the cells one operation may cover; the patch
 *   is then unchanged.
 */
function addCell(patch: OpenPatch, index: number, code: number, source: PatchCells, budget: number): boolean {
  const lastCells = patch.cells[patch.cells.length - 1];
  const lastValues = patch.values[patch.values.length - 1];
  const known = patch.slots.get(code);
  const slot = known ?? patch.palette.length;
  const extendsCells = lastCells !== undefined && lastCells[0] + lastCells[1] === index;
  const extendsValues = lastValues !== undefined && lastValues[0] === slot;
  const value = known === undefined ? source.valueOf(code) : null;
  let units = known === undefined ? JSON.stringify(value).length + 1 : 0;
  units += extendsCells ? digits(lastCells[1] + 1) - digits(lastCells[1]) : digits(index) + 5;
  units += extendsValues ? digits(lastValues[1] + 1) - digits(lastValues[1]) : digits(slot) + 5;
  if (
    patch.units + units > budget ||
    patch.count >= OP_CELLS_MAX ||
    (!extendsCells && patch.cells.length >= PATCH_CELL_RUNS_MAX) ||
    (!extendsValues && patch.values.length >= PATCH_VALUE_RUNS_MAX) ||
    (known === undefined && patch.palette.length >= PATCH_PALETTE_MAX)
  ) {
    return false;
  }
  if (known === undefined) {
    patch.slots.set(code, slot);
    patch.palette.push(value);
  }
  if (extendsCells) {
    lastCells[1] += 1;
  } else {
    patch.cells.push([index, 1]);
  }
  if (extendsValues) {
    lastValues[1] += 1;
  } else {
    patch.values.push([slot, 1]);
  }
  patch.units += units;
  patch.count += 1;
  return true;
}

/**
 * Run-encodes cells into cell patches of one layer's grid.
 *
 * @param map The map's id.
 * @param layer The layer's id.
 * @param grid The grid.
 * @param source The cells, in strictly ascending index order.
 * @returns The patches in cell order, none for no cells and never an empty one. Each is within
 *   `PATCH_CELL_RUNS_MAX`, `PATCH_VALUE_RUNS_MAX`, `PATCH_PALETTE_MAX` and `OP_CELLS_MAX`, and fits one operation frame by itself unless one value
 *   alone is longer than a frame, which leaves a patch of that one cell for the caller to refuse.
 */
export function patchesOf(map: string, layer: string, grid: PatchGrid, source: PatchCells): CellPatchIntent[] {
  const head = { k: "cells" as const, map, layer, grid };
  const budget = OP_FRAME_MAX - OP_FRAME_OVERHEAD - JSON.stringify({ ...head, palette: [], cells: [], values: [] }).length;
  const patches: CellPatchIntent[] = [];
  let patch = openPatch();
  for (let position = 0; position < source.count; position += 1) {
    const index = source.indexAt(position);
    const code = source.codeAt(position);
    if (!addCell(patch, index, code, source, budget)) {
      if (patch.count > 0) {
        patches.push({ ...head, palette: patch.palette, cells: patch.cells, values: patch.values });
      }
      patch = openPatch();
      addCell(patch, index, code, source, Number.POSITIVE_INFINITY);
    }
  }
  if (patch.count > 0) {
    patches.push({ ...head, palette: patch.palette, cells: patch.cells, values: patch.values });
  }
  return patches;
}

/**
 * Run-encodes a fill, cells that all take one value, into cell patches. It reads the indices a run at a time,
 * never a cell at a time, so a fill of a whole grid costs a pass over its indices.
 *
 * @param map The map's id.
 * @param layer The layer's id.
 * @param grid The grid.
 * @param indices The cells, in strictly ascending order.
 * @param value The value they take.
 * @returns The patches in cell order, none for no cells, each within `PATCH_CELL_RUNS_MAX` and `OP_CELLS_MAX`
 *   and short enough for one operation frame; a run that would pass the cells one operation may cover is cut.
 */
export function fillPatchesOf(map: string, layer: string, grid: PatchGrid, indices: ArrayLike<number>, value: PatchValue): CellPatchIntent[] {
  const head = { k: "cells" as const, map, layer, grid };
  const budget = OP_FRAME_MAX - OP_FRAME_OVERHEAD - JSON.stringify({ ...head, palette: [value], cells: [], values: [[0, OP_CELLS_MAX]] }).length;
  const patches: CellPatchIntent[] = [];
  let cells: [number, number][] = [];
  let units = 0;
  let count = 0;
  for (let i = 0; i < indices.length; ) {
    let start = indices[i] as number;
    let end = i + 1;
    while (end < indices.length && indices[end] === (indices[end - 1] as number) + 1) {
      end += 1;
    }
    for (let left = end - i; left > 0; ) {
      const take = Math.min(left, OP_CELLS_MAX - count);
      const cost = digits(start) + digits(take) + 4;
      if (take === 0 || (cells.length > 0 && (cells.length >= PATCH_CELL_RUNS_MAX || units + cost > budget))) {
        patches.push({ ...head, palette: [value], cells, values: [[0, count]] });
        cells = [];
        units = 0;
        count = 0;
      } else {
        cells.push([start, take]);
        units += cost;
        count += take;
        start += take;
        left -= take;
      }
    }
    i = end;
  }
  if (cells.length > 0) {
    patches.push({ ...head, palette: [value], cells, values: [[0, count]] });
  }
  return patches;
}
